import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Center,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import {
  Cake,
  Check,
  Copy,
  KeyRound,
  Pencil,
  QrCode,
  ScanLine,
  Settings,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import {
  PageScaffold,
  Section,
  SettingRow,
} from '../components/page/PageScaffold';
import { ConfirmSheet } from '../components/ConfirmSheet';
import { SignedInSessions } from '../components/SignedInSessions';
import { AvatarUpload } from '../components/family/AvatarUpload';
import { QrScanner } from '../components/auth/QrScanner';
import { useAuth, useFamily, useWebSocket, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { TwindenQr } from '../components/brand/TwindenQr';
import { WS_EVENTS, isValidBirthday } from '@twinden/shared';
import { shareKeys } from '../lib/e2ee/session';
import { api, ApiError, type Device } from '../lib/api';
import {
  addPasskeyHere,
  alreadyHasPasskey,
  deviceErrorMessage,
  guessDeviceName,
  makeDeviceLink,
} from '../lib/devices';
import { currentFamilyIdAtom } from '../store';
import { pictureErrorMessage, setMyPicture } from '../lib/pictureSetting';
import { appLocale } from '../lib/format';
import { useT, type MessageKey } from '../i18n';

// "22 October": a birthday has no year (D144).
function birthdayLabel(day: number, month: number): string {
  return new Date(2000, month - 1, day).toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'long',
  });
}

const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString(appLocale(), { month: 'long' })
);

const ROLE_LABEL: Record<string, MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

export function ProfilePage() {
  const { currentUser, setCurrentUser } = useAuth();
  const { families, loadFamilies } = useFamily();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();

  const [displayName, setDisplayName] = useState(
    currentUser?.displayName ?? ''
  );
  const [editing, setEditing] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  // The stored user may predate birthdays; ask for the current one (D144).
  useEffect(() => {
    let cancelled = false;
    api
      .getMe()
      .then((response) => {
        if (cancelled) return;
        const { birthDay, birthMonth } = response.data;
        setCurrentUser((user) =>
          user ? { ...user, birthDay, birthMonth } : user
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [setCurrentUser]);

  const [birthdayEditing, setBirthdayEditing] = useState(false);
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthdaySaving, setBirthdaySaving] = useState(false);
  const savedDay = currentUser?.birthDay ?? null;
  const savedMonth = currentUser?.birthMonth ?? null;
  const birthdayValid =
    birthDay !== '' &&
    birthMonth !== '' &&
    isValidBirthday(Number(birthDay), Number(birthMonth));

  const editBirthday = () => {
    setBirthDay(savedDay ? String(savedDay) : '');
    setBirthMonth(savedMonth ? String(savedMonth) : '');
    setBirthdayEditing(true);
  };

  const saveBirthday = async (clear = false) => {
    if (!clear && !birthdayValid) return;
    setBirthdaySaving(true);
    try {
      const response = await api.updateProfile({
        birthday: clear
          ? null
          : { day: Number(birthDay), month: Number(birthMonth) },
      });
      setCurrentUser((user) =>
        user
          ? {
              ...user,
              birthDay: response.data.birthDay,
              birthMonth: response.data.birthMonth,
            }
          : user
      );
      setBirthdayEditing(false);
    } catch (err) {
      toast({
        title: t('account.profile.birthdaySaveFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setBirthdaySaving(false);
    }
  };

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const response = await api.getMyDevices();
      setDevices(response.data);
    } catch {
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);
  // The small mark in the devices card while they load (M66, D250).
  const devicesState = useLoadingState({
    loading: devicesLoading,
    hasData: devices.length > 0,
    retry: () => void loadDevices(),
  });

  // Your picture, resized on this device first (issue #24), then sealed
  // once with its key wrapped for each of your families (D292).
  const savePicture = async (picture: string | null) => {
    if (!currentUser) return;
    try {
      const response = await setMyPicture(
        currentUser.id,
        picture,
        families.map((family) => family.id)
      );
      setCurrentUser((user) =>
        user ? { ...user, avatarUrl: response.data.avatarUrl } : user
      );
      toast({
        title: picture
          ? t('account.profile.pictureUpdated')
          : t('account.profile.pictureRemoved'),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('account.profile.updateFailed'),
        description: pictureErrorMessage(err),
        status: 'error',
      });
    }
  };

  const saveDisplayName = async () => {
    if (!displayName.trim()) return;
    try {
      const response = await api.updateProfile({
        displayName: displayName.trim(),
      });
      setCurrentUser((user) =>
        user ? { ...user, displayName: response.data.displayName } : user
      );
      setEditing(false);
      toast({ title: t('account.profile.updated'), status: 'success' });
    } catch (err) {
      toast({
        title: t('account.profile.updateFailed'),
        description:
          err instanceof ApiError
            ? err.message
            : t('account.profile.unknownError'),
        status: 'error',
      });
    }
  };

  // Rename and remove devices in the app's own sheets, not browser popups.
  const [renaming, setRenaming] = useState<Device | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [revoking, setRevoking] = useState<Device | null>(null);
  // Removing a passkey signs its sessions out too (D214).
  const [sessionsKey, setSessionsKey] = useState(0);

  const renameDevice = async () => {
    if (!renaming || !deviceName.trim()) return;
    try {
      await api.renameDevice(renaming.id, deviceName.trim());
      setRenaming(null);
      await loadDevices();
    } catch (err) {
      toast({
        title: t('account.profile.renameFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    }
  };

  const revokeDevice = async () => {
    const device = revoking;
    if (!device) return;
    setRevoking(null);
    try {
      await api.revokeDevice(device.id);
      await loadDevices();
      setSessionsKey((key) => key + 1);
      toast({ title: t('account.profile.deviceRemoved'), status: 'success' });
    } catch (err) {
      toast({
        title: t('account.profile.removeFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    }
  };

  // Adding your own devices (M41, docs/adding-devices.md).
  const currentFamilyId = useAtomValue(currentFamilyIdAtom);
  // Your own face opens through a family you're in, like anyone's (D292).
  const pictureFamilyId = currentFamilyId ?? families[0]?.id ?? null;
  const [link, setLink] = useState<{
    joinUrl: string;
    qrDataUrl: string;
    expiresAt: string;
  } | null>(null);
  const [making, setMaking] = useState(false);
  // Sign in by scanning: this phone reads a computer's code (M49, D158).
  const [scanning, setScanning] = useState(false);
  const [addingHere, setAddingHere] = useState(false);
  // D138: this screen waits for the new device and hands it the keys.
  const [handover, setHandover] = useState<
    { status: 'waiting' } | { status: 'sent'; chats: number } | null
  >(null);
  const handoverRef = useRef(handover);
  handoverRef.current = handover;
  const { onEvent } = useWebSocket();
  const myId = currentUser?.id;
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.KEYS_NEEDED) return;
        // Our own new device registered its key.
        if ((payload as { userId?: string }).userId !== myId) return;
        if (handoverRef.current?.status !== 'waiting') return;
        void shareKeys().then(({ chats }) =>
          setHandover({ status: 'sent', chats })
        );
      }),
    [onEvent, myId]
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!link) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [link]);

  const deviceError = (title: string, err: unknown) => {
    const description = deviceErrorMessage(err);
    if (description) toast({ title, description, status: 'error' });
  };

  const makeLink = async () => {
    setMaking(true);
    try {
      setLink(await makeDeviceLink(currentFamilyId));
      setHandover({ status: 'waiting' });
      setNow(Date.now());
    } catch (err) {
      deviceError(t('account.profile.linkFailed'), err);
    } finally {
      setMaking(false);
    }
  };

  const closeLink = () => {
    setLink(null);
    setHandover(null);
    // The new device may have signed in meanwhile.
    void loadDevices();
  };

  const cancelLink = async () => {
    try {
      await api.cancelDeviceLink();
    } catch {
      // It expires in minutes anyway.
    }
    closeLink();
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.joinUrl);
      toast({
        title: t('account.profile.linkCopied'),
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({ title: t('account.profile.copyFailed'), status: 'error' });
    }
  };

  // A device that already has a passkey refuses another, unless asked:
  // that's how you move it into a password manager such as NordPass.
  const [offerAnother, setOfferAnother] = useState(false);
  const passkeyHere = async (another = false) => {
    setOfferAnother(false);
    setAddingHere(true);
    try {
      await addPasskeyHere(guessDeviceName(), another);
      await loadDevices();
      toast({
        title: t('account.profile.passkeyCreated'),
        status: 'success',
      });
    } catch (err) {
      if (!another && alreadyHasPasskey(err)) setOfferAnother(true);
      else deviceError(t('account.passkeyHere.failed'), err);
    } finally {
      setAddingHere(false);
    }
  };

  const secondsLeft = link
    ? Math.max(0, Math.round((new Date(link.expiresAt).getTime() - now) / 1000))
    : 0;
  const timeLeft = `${Math.floor(secondsLeft / 60)}:${String(
    secondsLeft % 60
  ).padStart(2, '0')}`;

  const day = (iso: string) =>
    new Date(iso).toLocaleDateString(appLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  return (
    <PageScaffold title={t('account.menu.profile')}>
      <VStack spacing={3} pb={5} textAlign="center">
        <AvatarUpload
          value={currentUser?.avatarUrl}
          onChange={(picture) => void savePicture(picture)}
          size="lg"
          name={currentUser?.displayName}
          // Opened through the family on screen elsewhere (D292).
          ownerId={currentUser?.id}
          familyId={pictureFamilyId}
        />
        {editing ? (
          <HStack w="full" maxW="360px" spacing={2}>
            <Input
              h="48px"
              autoFocus
              aria-label={t('account.profile.yourName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveDisplayName();
              }}
            />
            <IconButton
              aria-label={t('account.profile.saveName')}
              icon={<Check size={18} />}
              onClick={saveDisplayName}
            />
            <IconButton
              aria-label={t('common.cancel')}
              icon={<X size={18} />}
              variant="ghost"
              colorScheme="gray"
              onClick={() => {
                setDisplayName(currentUser?.displayName ?? '');
                setEditing(false);
              }}
            />
          </HStack>
        ) : (
          <VStack spacing={1}>
            <Heading as="h2" fontSize="28px">
              {currentUser?.displayName}
            </Heading>
            <Text fontSize="16px" color="muted">
              @{currentUser?.handle}
            </Text>
            <Button
              mt={2}
              h="40px"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              leftIcon={<Pencil size={16} />}
              onClick={() => setEditing(true)}
            >
              {t('account.profile.changeName')}
            </Button>
          </VStack>
        )}
      </VStack>

      <Section
        title={t('account.profile.birthday')}
        description={t('account.profile.birthdayDescription')}
      >
        {birthdayEditing ? (
          <VStack align="stretch" spacing={3}>
            <HStack spacing={2} align="flex-end">
              <Box flex="2">
                <Text
                  as="label"
                  htmlFor="birthday-month"
                  fontSize="14px"
                  fontWeight={700}
                  color="muted"
                >
                  {t('account.profile.month')}
                </Text>
                <Select
                  id="birthday-month"
                  h="48px"
                  mt={1}
                  borderRadius="xl"
                  placeholder={t('account.profile.month')}
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                >
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box flex="1">
                <Text
                  as="label"
                  htmlFor="birthday-day"
                  fontSize="14px"
                  fontWeight={700}
                  color="muted"
                >
                  {t('account.profile.day')}
                </Text>
                <Select
                  id="birthday-day"
                  h="48px"
                  mt={1}
                  borderRadius="xl"
                  placeholder={t('account.profile.day')}
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option
                      key={day}
                      value={day}
                      disabled={
                        birthMonth !== '' &&
                        !isValidBirthday(day, Number(birthMonth))
                      }
                    >
                      {day}
                    </option>
                  ))}
                </Select>
              </Box>
            </HStack>
            {birthDay !== '' && birthMonth !== '' && !birthdayValid && (
              <Text fontSize="14px" color="danger">
                {t('account.profile.noSuchDay', {
                  month: MONTHS[Number(birthMonth) - 1] ?? '',
                  day: birthDay,
                })}
              </Text>
            )}
            <HStack spacing={2}>
              <Button
                h="44px"
                flex="1"
                onClick={() => void saveBirthday()}
                isDisabled={!birthdayValid}
                isLoading={birthdaySaving}
              >
                {t('common.save')}
              </Button>
              <Button
                h="44px"
                flex="1"
                variant="outline"
                colorScheme="gray"
                borderColor="line"
                onClick={() => setBirthdayEditing(false)}
              >
                {t('common.cancel')}
              </Button>
            </HStack>
          </VStack>
        ) : (
          <SettingRow
            label={
              <HStack as="span" spacing={2}>
                <Cake size={16} aria-hidden="true" />
                <span>
                  {savedDay && savedMonth
                    ? birthdayLabel(savedDay, savedMonth)
                    : t('account.profile.notAdded')}
                </span>
              </HStack>
            }
          >
            {savedDay && savedMonth && (
              <Button
                h="44px"
                variant="ghost"
                colorScheme="gray"
                color="danger"
                onClick={() => void saveBirthday(true)}
                isLoading={birthdaySaving}
                aria-label={t('account.profile.removeBirthday')}
              >
                {t('common.remove')}
              </Button>
            )}
            <Button
              h="44px"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              onClick={editBirthday}
              aria-label={
                savedDay && savedMonth
                  ? t('account.profile.changeBirthday')
                  : t('account.profile.addBirthday')
              }
            >
              {savedDay && savedMonth
                ? t('account.profile.change')
                : t('account.profile.add')}
            </Button>
          </SettingRow>
        )}
      </Section>

      <Section
        title={t('account.profile.families')}
        description={t('account.profile.familiesDescription')}
      >
        {families.length === 0 ? (
          <Text color="muted">{t('account.profile.noFamilies')}</Text>
        ) : (
          families.map((family) => (
            <SettingRow
              key={family.id}
              label={family.name}
              hint={
                ROLE_LABEL[family.role]
                  ? t(ROLE_LABEL[family.role]!)
                  : undefined
              }
            >
              {family.role !== 'member' && (
                <Button
                  h="40px"
                  variant="outline"
                  colorScheme="gray"
                  borderColor="line"
                  leftIcon={<Settings size={16} />}
                  onClick={() => navigate(`/family/${family.id}/admin`)}
                >
                  {t('account.profile.manage')}
                </Button>
              )}
            </SettingRow>
          ))
        )}
      </Section>

      <Section
        title={t('account.profile.devices')}
        description={t('account.profile.devicesDescription')}
      >
        {devicesState.show ? (
          <LoadingPanel state={devicesState} size="inline" />
        ) : devices.length === 0 ? (
          <Text color="muted">{t('account.profile.noDevices')}</Text>
        ) : (
          devices.map((device) => (
            <SettingRow
              key={device.id}
              label={
                <HStack as="span" spacing={2}>
                  <Smartphone size={16} aria-hidden="true" />
                  <span>
                    {device.deviceName ?? t('account.profile.unnamedDevice')}
                  </span>
                </HStack>
              }
              hint={
                device.lastUsedAt
                  ? t('account.profile.addedUsed', {
                      added: day(device.createdAt),
                      used: day(device.lastUsedAt),
                    })
                  : t('account.profile.added', { added: day(device.createdAt) })
              }
            >
              <IconButton
                aria-label={t('account.profile.renameNamed', {
                  name: device.deviceName ?? t('account.profile.device'),
                })}
                icon={<Pencil size={16} />}
                variant="ghost"
                colorScheme="gray"
                onClick={() => {
                  setDeviceName(device.deviceName ?? '');
                  setRenaming(device);
                }}
              />
              <IconButton
                aria-label={t('account.profile.removeNamed', {
                  name: device.deviceName ?? t('account.profile.device'),
                })}
                icon={<Trash2 size={16} />}
                variant="ghost"
                colorScheme="gray"
                color="danger"
                onClick={() => setRevoking(device)}
              />
            </SettingRow>
          ))
        )}
        <VStack align="stretch" spacing={2} pt={3}>
          <Button
            leftIcon={<QrCode size={18} />}
            onClick={() => void makeLink()}
            isLoading={making}
            loadingText={t('account.profile.confirmItsYou')}
          >
            {t('account.profile.addDevice')}
          </Button>
          <Button
            variant="outline"
            colorScheme="gray"
            leftIcon={<ScanLine size={18} />}
            onClick={() => setScanning(true)}
          >
            {t('account.profile.scanToSignIn')}
          </Button>
          <Button
            variant="outline"
            colorScheme="gray"
            leftIcon={<KeyRound size={18} />}
            onClick={() => void passkeyHere()}
            isLoading={addingHere}
            loadingText={t('account.profile.waitingDevice')}
          >
            {t('account.profile.passkeyHere')}
          </Button>
        </VStack>
        <SignedInSessions refreshKey={sessionsKey} />
      </Section>

      <QrScanner
        isOpen={scanning}
        onClose={() => setScanning(false)}
        onFound={(path) => {
          setScanning(false);
          navigate(path);
        }}
      />

      <Modal isOpen={link !== null} onClose={closeLink} isCentered>
        <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
        <ModalContent mx={4} borderRadius="28px">
          <ModalHeader>
            <Heading as="h2" fontSize="22px">
              {t('account.profile.addDevice')}
            </Heading>
          </ModalHeader>
          <ModalBody>
            {handover?.status === 'sent' ? (
              <VStack spacing={3} py={2} textAlign="center">
                <Center
                  w="56px"
                  h="56px"
                  borderRadius="full"
                  bg="accent.tint"
                  color="accent"
                >
                  <Check size={28} />
                </Center>
                <Text fontWeight={700} fontSize="18px">
                  {handover.chats > 0
                    ? handover.chats === 1
                      ? t('account.profile.keysSentOne')
                      : t('account.profile.keysSentOther', {
                          count: handover.chats,
                        })
                    : t('account.profile.deviceReady')}
                </Text>
                <Text color="muted">
                  {t('account.profile.deviceReadyHint')}
                </Text>
              </VStack>
            ) : secondsLeft > 0 ? (
              <VStack spacing={4}>
                {link && (
                  <TwindenQr
                    value={link.joinUrl}
                    label={t('account.profile.qrAlt')}
                    size="232px"
                  />
                )}
                <Text textAlign="center">
                  {t('account.profile.linkHint')}{' '}
                  <Text as="span" fontWeight={700}>
                    {timeLeft}
                  </Text>
                  {t('account.profile.linkHintEnd')}
                </Text>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  leftIcon={<Copy size={16} />}
                  onClick={() => void copyLink()}
                >
                  {t('account.profile.copyLink')}
                </Button>
                <HStack
                  spacing={2}
                  color="muted"
                  fontSize="sm"
                  justify="center"
                  textAlign="center"
                >
                  <Spinner size="xs" />
                  <Text>{t('account.profile.waitingNewDevice')}</Text>
                </HStack>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Text textAlign="center">
                  {t('account.profile.linkExpired')}
                </Text>
                <Button onClick={() => void makeLink()} isLoading={making}>
                  {t('account.profile.newLink')}
                </Button>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            {secondsLeft > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                onClick={() => void cancelLink()}
              >
                {t('account.profile.cancelLink')}
              </Button>
            )}
            <Button onClick={closeLink}>{t('common.done')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={renaming !== null}
        onClose={() => setRenaming(null)}
        isCentered
      >
        <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
        <ModalContent mx={4} borderRadius="28px">
          <ModalHeader>
            <Heading as="h2" fontSize="22px">
              {t('account.profile.renameDevice')}
            </Heading>
          </ModalHeader>
          <ModalBody>
            <Input
              h="48px"
              autoFocus
              aria-label={t('account.profile.deviceName')}
              value={deviceName}
              maxLength={100}
              onChange={(e) => setDeviceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void renameDevice();
              }}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={() => setRenaming(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              isDisabled={!deviceName.trim()}
              onClick={() => void renameDevice()}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmSheet
        isOpen={revoking !== null}
        onClose={() => setRevoking(null)}
        icon={<Smartphone size={28} />}
        centered
        title={t('account.profile.removeTitle', {
          name: revoking?.deviceName ?? t('account.profile.thisDevice'),
        })}
        body={t('account.profile.removeBody')}
        confirmLabel={t('account.profile.removeDevice')}
        cancelLabel={t('account.profile.keepIt')}
        onConfirm={() => void revokeDevice()}
      />

      <ConfirmSheet
        isOpen={offerAnother}
        onClose={() => setOfferAnother(false)}
        icon={<KeyRound size={28} />}
        centered
        title={t('account.profile.anotherPasskeyTitle')}
        body={t('account.profile.anotherPasskeyBody')}
        confirmLabel={t('account.profile.anotherPasskeyCreate')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void passkeyHere(true)}
      />
    </PageScaffold>
  );
}
